import React, { useCallback } from 'react';
import { Modal } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { getRatingsVisible } from 'logic/store/modal/modal.selectors';
import { modalSlice } from 'logic/store/modal/modal.slice';
import { UserReviewList } from 'components/rating/UserReviewList';
import {
  getUserBadges,
  getUserRatings,
  getOfferAverageRating,
  getOfferTotalRatings,
} from 'logic/store/rating/rating.selectors';
import { RatingBadgesGroup } from 'components/rating/RatingBadgesGroup';
import { RatingBadge } from 'components/rating/RatingBadge';
import { BadgesNames } from 'models/badges';
import styled from 'styled-components';
import { Text } from 'components/text/Text';
import { translate, translationKeys } from 'logic/translations/translations.service';
import { StarsRating } from 'components/rating/StarsRating';
import { formatAverageRating } from 'logic/utils/number';

const BadgesWrapper = styled.div`
  margin-top: 36px;
  margin-bottom: 24px;
`;

const RatingsSpan = styled.span`
  display: flex;
  align-items: center;
`;

interface RatingsModalProps {}

export const RatingsModal: React.FC<RatingsModalProps> = () => {
  const dispatch = useDispatch();
  const visible = useSelector(getRatingsVisible);
  const userRatings = useSelector(getUserRatings);
  const averageRating = useSelector(getOfferAverageRating);
  const totalRatings = useSelector(getOfferTotalRatings);
  const badges = useSelector(getUserBadges);
  const handleClose = useCallback(() => dispatch(modalSlice.actions.closeReviewsModal()), [
    dispatch,
  ]);

  const renderRatingsIfCan = () =>
    totalRatings > 0 && (
      <RatingsSpan>
        <StarsRating value={averageRating} allowChange={false} size={14} spaceStars={4} />
        <Text size={14} strong style={{ marginLeft: 14 }}>
          {formatAverageRating(averageRating)} ({totalRatings}{' '}
          {translate(translationKeys.tutorDetails.info.reviews)})
        </Text>
      </RatingsSpan>
    );

  const renderBadgesIfCan = () => {
    const hasAnyBadge = Object.values(badges).some((count) => count || 0 > 0);

    return (
      hasAnyBadge && (
        <BadgesWrapper>
          <RatingBadgesGroup inline>
            {Object.entries(badges)
              .map(
                ([badge, count], idx) =>
                  count && <RatingBadge key={idx} badge={badge as BadgesNames} count={count} />
              )
              .filter(Boolean)}
          </RatingBadgesGroup>
        </BadgesWrapper>
      )
    );
  };

  return (
    <Modal
      visible={visible}
      onCancel={handleClose}
      maskClosable
      footer={null}
      centered
      width={'100%'}
      style={{
        marginTop: 10,
        marginBottom: 10,
        maxWidth: 984,
      }}
    >
      {renderRatingsIfCan()}
      {renderBadgesIfCan()}
      <UserReviewList userRatings={userRatings} />
    </Modal>
  );
};
